(() => {
  const SLOT_SELECTOR = '[data-collection-marketplace]';
  const GRID_SELECTOR = 'results-list [ref="grid"], .product-grid';
  const POS_KEYS = {
    mobile: 'posMobile',
    tablet: 'posTablet',
    desktop: 'posDesktop',
  };
  const BREAKPOINT_QUERIES = {
    mobile: '(max-width: 749px)',
    tablet: '(min-width: 750px) and (max-width: 1199px)',
    desktop: '(min-width: 1200px)',
  };

  let gridObserver = null;
  let placeScheduled = false;

  const getBreakpoint = () => {
    if (window.matchMedia(BREAKPOINT_QUERIES.desktop).matches) return 'desktop';
    if (window.matchMedia(BREAKPOINT_QUERIES.tablet).matches) return 'tablet';
    return 'mobile';
  };

  const getGrid = () => document.querySelector(GRID_SELECTOR);

  const markLoaded = (slot) => {
    if (!(slot instanceof HTMLElement)) return;
    slot.classList.add('is-loaded');
  };

  const initSlot = (slot) => {
    if (!(slot instanceof HTMLElement) || slot.dataset.mpInit === 'true') return;
    slot.dataset.mpInit = 'true';

    const images = slot.querySelectorAll('[data-marketplace-content] img');
    if (!images.length) {
      markLoaded(slot);
      return;
    }

    let remaining = images.length;
    const onDone = () => {
      remaining -= 1;
      if (remaining <= 0) markLoaded(slot);
    };

    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) {
        onDone();
        return;
      }
      if (image.complete) {
        onDone();
        return;
      }
      image.addEventListener('load', onDone, { once: true });
      image.addEventListener('error', onDone, { once: true });
    });

    window.setTimeout(() => markLoaded(slot), 2500);
  };

  const placeSlot = (slot) => {
    const grid = getGrid();
    if (!(grid instanceof HTMLElement) || !(slot instanceof HTMLElement)) return;

    const breakpoint = getBreakpoint();
    const position = Number(slot.dataset[POS_KEYS[breakpoint]] || 0);

    if (!position) {
      slot.remove();
      return;
    }

    const target = grid.querySelector(`:scope > [data-product-index="${position}"]`);
    if (!(target instanceof HTMLElement)) {
      slot.remove();
      return;
    }

    if (target.nextElementSibling !== slot) {
      target.after(slot);
    }

    initSlot(slot);
  };

  const placeAll = () => {
    document.querySelectorAll(SLOT_SELECTOR).forEach(placeSlot);
  };

  const schedulePlace = () => {
    if (placeScheduled) return;
    placeScheduled = true;
    window.requestAnimationFrame(() => {
      placeScheduled = false;
      placeAll();
    });
  };

  const observeGrid = () => {
    const grid = getGrid();
    if (!(grid instanceof HTMLElement)) return;
    if (gridObserver) gridObserver.disconnect();

    gridObserver = new MutationObserver(schedulePlace);
    gridObserver.observe(grid, { childList: true });
  };

  const bindBreakpointChanges = () => {
    Object.values(BREAKPOINT_QUERIES).forEach((query) => {
      const media = window.matchMedia(query);
      const onChange = (event) => {
        if (event.matches) placeAll();
      };
      if (typeof media.addEventListener === 'function') {
        media.addEventListener('change', onChange);
      } else {
        media.addListener(onChange);
      }
    });
  };

  const getMarketplaceUserId = () => {
    if (typeof getUserData === 'function') {
      const data = getUserData();
      if (data && data.pw_user_id) return String(data.pw_user_id);
    }
    const customerData = window.__CUSTOMER_DATA__ || {};
    if (customerData.pw_user_id) return String(customerData.pw_user_id);
    return '';
  };

  const appendUserIdToUrl = (href) => {
    const userId = getMarketplaceUserId();
    if (!href || !userId) return href;

    try {
      const url = new URL(href, window.location.origin);
      if (!url.searchParams.get('userId')) {
        url.searchParams.append('userId', userId);
      }
      return url.toString();
    } catch (e) {
      return href;
    }
  };

  const bindUserIdOnMarketplaceLinks = () => {
    if (window.__PW_MARKETPLACE_USERID_BOUND__) return;
    window.__PW_MARKETPLACE_USERID_BOUND__ = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const link = target.closest(
          '.collection-marketplace-card[href], .collection-marketplace__view-all[href]'
        );
        if (!(link instanceof HTMLAnchorElement)) return;

        const nextHref = appendUserIdToUrl(link.getAttribute('href'));
        if (nextHref) {
          link.setAttribute('href', nextHref);
        }

        trackPrepOnlineIngressClicked(link);
      },
      true
    );
  };

  const getMarketplaceUtmParams = (destinationUrl) => {
    const utm = {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
    };

    if (destinationUrl) {
      try {
        const parsed = new URL(destinationUrl, window.location.origin);
        utm.utm_source = parsed.searchParams.get('utm_source');
        utm.utm_medium = parsed.searchParams.get('utm_medium');
        utm.utm_campaign = parsed.searchParams.get('utm_campaign');
      } catch (e) {}
    }

    const urlAttrs =
      window.pwURLAttributes && typeof window.pwURLAttributes.get === 'function'
        ? window.pwURLAttributes
        : null;
    if (urlAttrs) {
      ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => {
        if (!utm[key]) {
          const value = urlAttrs.get(key);
          if (value) utm[key] = value;
        }
      });
    }

    return utm;
  };

  const getDestinationPageType = (href) => {
    if (!href) return null;
    try {
      const path = new URL(href, window.location.origin).pathname.toLowerCase();
      if (path.includes('/products/')) return 'product';
      if (path.includes('/collections/')) return 'category';
      return 'home';
    } catch (e) {
      return null;
    }
  };

  const getCategoryNameForIngress = () => {
    const pageContext =
      typeof getPageContext === 'function' ? getPageContext() : { pageName: null, pageSubtype: null };
    const pageName =
      pageContext.pageName ||
      (typeof getPageNameFromUrl === 'function' ? getPageNameFromUrl() : null);

    if (pageName !== 'Collection' && pageName !== 'Product' && pageName !== 'Home') {
      return null;
    }

    const subtype = pageContext.pageSubtype;
    if (subtype && subtype !== 'Collection' && subtype !== 'HomePage' && subtype !== 'Product') {
      return subtype;
    }

    const path = (window.location.pathname || '').toLowerCase();
    if (path.includes('neet')) return 'NEET';
    if (path.includes('jee')) return 'JEE';
    if (path.includes('cbse')) return 'CBSE';
    if (path.includes('icse')) return 'ICSE';
    return null;
  };

  const buildProductRedirectionLinkAttrs = (productLinks) => {
    const attrs = {};
    if (!Array.isArray(productLinks)) return attrs;
    for (let i = 0; i < productLinks.length; i += 1) {
      attrs[`product_${i + 1}_redirection_link`] = productLinks[i] || null;
    }
    return attrs;
  };

  const getIngressContext = (sectionEl, slotEl) => {
    if (!(sectionEl instanceof HTMLElement)) return null;

    const widgetName = sectionEl.dataset.ingressWidgetName || 'prep_online_ingress';
    const ingressTitle = sectionEl.dataset.ingressTitle || null;
    const destinationStore = sectionEl.dataset.destinationStore || 'prep_online';
    const ingressPosition = sectionEl.dataset.ingressPosition || slotEl?.dataset?.marketplaceIndex || null;
    const rawDestinationUrl = sectionEl.dataset.destinationUrl || null;
    const destinationUrl = rawDestinationUrl ? appendUserIdToUrl(rawDestinationUrl) : null;

    const productLinks = [];
    sectionEl.querySelectorAll('.collection-marketplace-card[href]').forEach((card) => {
      if (!(card instanceof HTMLAnchorElement)) return;
      const href = appendUserIdToUrl(card.getAttribute('href'));
      if (href) productLinks.push(href);
    });

    const payload = {
      widget_name: widgetName,
      ingress_title: ingressTitle,
      ingress_position: ingressPosition != null && ingressPosition !== '' ? Number(ingressPosition) : null,
      destination_store: destinationStore,
      destination_url: destinationUrl,
      ...buildProductRedirectionLinkAttrs(productLinks),
      ...getMarketplaceUtmParams(destinationUrl),
    };

    return payload;
  };

  const pushPrepOnlineIngressEvent = (gaEventName, additionalData) => {
    window.dataLayer = window.dataLayer || [];
    const pageName =
      typeof getPageContext === 'function' ? getPageContext().pageName : null;

    if (typeof buildBaseGtmEventData === 'function') {
      window.dataLayer.push(
        buildBaseGtmEventData(gaEventName, additionalData, { pageName })
      );
    }

    if (typeof buildBaseMoeEventData === 'function') {
      const moeEventName = String(gaEventName).replace(/^ga_/, 'moe_');
      window.dataLayer.push(buildBaseMoeEventData(moeEventName, additionalData));
    }
  };

  const trackPrepOnlineIngressViewed = (sectionEl, slotEl) => {
    const payload = getIngressContext(sectionEl, slotEl);
    if (!payload) return;
    pushPrepOnlineIngressEvent('ga_pwst_prep_online_ingress_viewed', payload);
  };

  const trackPrepOnlineIngressClicked = (link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const sectionEl = link.closest('[data-marketplace-content]');
    const slotEl = link.closest(SLOT_SELECTOR);
    const payload = getIngressContext(sectionEl, slotEl);
    if (!payload) return;

    const href = appendUserIdToUrl(link.getAttribute('href'));
    const ingressVariant = link.classList.contains('collection-marketplace__view-all')
      ? 'view_all_cta'
      : 'product_card';

    pushPrepOnlineIngressEvent('ga_pwst_prep_online_ingress_clicked', {
      ...payload,
      ingress_variant: ingressVariant,
      destination_url: href || payload.destination_url,
      destination_page_type: getDestinationPageType(href || payload.destination_url),
      category_name: getCategoryNameForIngress(),
    });
  };

  const bindPrepOnlineIngressViewTracking = () => {
    if (window.__PW_PREP_ONLINE_INGRESS_VIEW_BOUND__) return;
    window.__PW_PREP_ONLINE_INGRESS_VIEW_BOUND__ = true;

    const viewedKeys = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.25) return;

          const sectionEl = entry.target;
          if (!(sectionEl instanceof HTMLElement)) return;
          const slotEl = sectionEl.closest(SLOT_SELECTOR);
          const viewKey = [
            slotEl?.dataset?.marketplaceIndex || '',
            slotEl?.dataset?.page || '',
          ].join(':');
          if (!viewKey || viewedKeys.has(viewKey)) return;

          viewedKeys.add(viewKey);
          trackPrepOnlineIngressViewed(sectionEl, slotEl);
          observer.unobserve(sectionEl);
        });
      },
      { threshold: [0.25] }
    );

    const observeSections = () => {
      document.querySelectorAll('[data-marketplace-content]').forEach((sectionEl) => {
        if (!(sectionEl instanceof HTMLElement)) return;
        const slotEl = sectionEl.closest(SLOT_SELECTOR);
        const viewKey = [
          slotEl?.dataset?.marketplaceIndex || '',
          slotEl?.dataset?.page || '',
        ].join(':');
        if (!viewKey || viewedKeys.has(viewKey)) return;
        observer.observe(sectionEl);
      });
    };

    observeSections();

    const grid = getGrid();
    if (grid instanceof HTMLElement) {
      const contentObserver = new MutationObserver(() => {
        observeSections();
      });
      contentObserver.observe(grid, { childList: true, subtree: true });
    }
  };

  const initPrepOnlineIngressTracking = () => {
    if (
      typeof buildBaseGtmEventData !== 'function' &&
      typeof buildBaseMoeEventData !== 'function'
    ) {
      return;
    }
    bindPrepOnlineIngressViewTracking();
  };

  const start = () => {
    placeAll();
    observeGrid();
    bindBreakpointChanges();
    bindUserIdOnMarketplaceLinks();
    initPrepOnlineIngressTracking();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('load', () => {
    initPrepOnlineIngressTracking();
  }, { once: true });

  document.addEventListener('filter:update', () => {
    window.requestAnimationFrame(() => {
      placeAll();
      observeGrid();
    });
  });
})();
